import type {
  Book,
  BookListItem,
  Doc,
  DocListItem,
  Annotation,
  AnnotationContent,
  Billing,
  Comment,
  Exercise,
  Plan,
  ThreadWithAuthor,
  Thread,
  ExcerptData,
  CustomList,
  ListItemType,
  ListItem,
  Changelog,
} from '@/models/interfaces'
import type { QuestionnaireUserChoices } from '@/models/onboarding.types'
import { ContentView, DocSortOption, DocView } from '@/models/doc'
import { platform } from '@/services/platform'
import { Sentry } from '@/services/sentry'
import { offline } from '@/services/offline'
import { docTypeLabel } from '@/models/doc'
import { capitalizeString } from '@/helpers/text'
import { auth } from '@/services/auth'
import { NotificationType } from '@/services/notification'
import { assertNotNull } from '@/helpers/typing'

import {
  hasMixpanel,
  pageView as basePageView,
  _track as baseTrack,
  _trackAsync as baseTrackAsync,
  setOnce,
  getMixpanelSuperProperties,
} from './base'
import { router } from '@/helpers/router'
import { daysDiff } from '@/helpers/time.native'
import { checklist } from '@/models/checklist'
import type { CompletionRecommendationData } from '@/models/completion'
import type { CompletionPath } from '@/services/completion'
import type { GuideCompletion19383Variant } from '@/services/ab/types'
import type { AudioOutputDevice } from '@/services/capacitor.audioOutputDeviceDetectionPlugin'

export type CompletionFlowAction =
  | 'close'
  | 'continue'
  | 'open-highlight'
  | 'add-highlight'
  | 'open-exercise'
  | 'open-recommendation'
  | 'favorite-recommendation'
  | 'unfavorite-recommendation'
  | 'open-highlights'
  | 'open-exercises'
  | 'save'
  | 'download'
  | 'buy-on-amazon'
  | 'discuss'
  | 'mark-as-not-started'

export function forgotPassword(): void {
  _track('Forgot Password Request')
}

export function resetPassword(): void {
  _track('Forgot Password Reset')
}

export async function pageView(params?: Record<string, any>): Promise<void> {
  try {
    await _setSuperProperties()
  } catch (error) {
    // Sentry is not loaded during prerendering.
    Sentry?.captureException(error)
  }
  basePageView(params)
}

export function trackBookmarkItemTOCClick(bookmark: Annotation): void {
  _track(
    'Click Bookmark Item List',
    {
      bookmark: bookmark.id,
    },
    {
      'Last Click Bookmark Item List': new Date().toISOString(),
    },
    {
      'Click Bookmark Item List Count': 1,
    },
  )
}

export function trackSidebarItemClick(item: string): void {
  _track(
    'Click Reading Sidebar Item',
    {
      item: item,
    },
    {
      [`Last Click Reading Sidebar ${item} Click`]: new Date().toISOString(),
    },
    {
      [`Click Reading Sidebar ${item} Click Count`]: 1,
    },
  )
}
export function trackCopyButtonClickReadingPage(): void {
  _track('Copy Content Button Click')

  _track(
    'Copy Content Button Click',
    {
      'Last Copy Content Button Click': new Date().toISOString(),
    },
    {
      'Copy Content Button Clicked': 1,
    },
  )
}

/**
 * Account context attached to every cancel flow event.
 */
function cancelFlowContext(billing: Billing): Record<string, unknown> {
  const user = auth.getUser()
  return {
    'Is Trial': billing.is_trial,
    'Tenure Days': user ? daysDiff(user.signup) : null,
    'Has Ever Had Discount': billing.has_ever_had_discount,
  }
}

/**
 * Properties shared by every cancel save flow event so they group consistently
 * in funnels. `step` is the retention step the user reached.
 */
function cancelFlowEventData(
  billing: Billing,
  reason: string | null = null,
  step: string | null = null,
): Record<string, unknown> {
  return {
    'Cancelation Reason': reason,
    'Save Offer Branch': step,
    ...cancelFlowContext(billing),
  }
}

export function trackCancelSubscriptionClick(billing?: Billing): void {
  _track(
    'Cancel Subscription Click',
    {
      ...(billing ? cancelFlowEventData(billing) : {}),
      'Last Cancel Subscription Step': new Date().toISOString(),
    },
    {
      'Cancel Subscription Steps': 1,
    },
  )
}

export function trackCancelReasonSubmitted(
  billing: Billing,
  reason: string,
  step: string,
): void {
  _track('Cancel Reason Submitted', cancelFlowEventData(billing, reason, step))
}

export function trackCancelOfferShown(
  billing: Billing,
  reason: string | null,
  step: string,
): void {
  _track('Cancel Offer Shown', cancelFlowEventData(billing, reason, step))
}

export function trackCancelOfferAccepted(
  billing: Billing,
  reason: string | null,
  step: string,
): void {
  _track('Cancel Offer Accepted', cancelFlowEventData(billing, reason, step))
}

export function trackCancelOfferDeclined(
  billing: Billing,
  reason: string | null,
  step: string,
): void {
  _track('Cancel Offer Declined', cancelFlowEventData(billing, reason, step))
}

export function trackCancelFlowClosed(
  billing: Billing,
  reason: string | null,
  step: string,
): void {
  _track('Cancel Flow Closed', cancelFlowEventData(billing, reason, step))
}

/**
 * A cancel flow step was shown. Together with `Cancel Flow Button Click` it
 * shows where users leave the flow without pressing any button.
 */
export function trackCancelFlowStepShown(
  billing: Billing,
  step: string,
  reason: string | null = null,
): void {
  _track('Cancel Flow Step Shown', {
    Step: step,
    ...cancelFlowEventData(billing, reason),
  })
}

export function trackCancelFlowButtonClick(
  billing: Billing,
  step: string,
  button: string,
  reason: string | null = null,
): void {
  _track('Cancel Flow Button Click', {
    Step: step,
    Button: button,
    ...cancelFlowEventData(billing, reason),
  })
}

export function trackBilling(
  new_plan: Plan,
  billing: Billing,
  done: () => void,
): void {
  _track(
    'Billing Change',
    {
      from: billing.plan_id,
      to: new_plan.id,
      to_amount: new_plan.amount,
      to_nickname: new_plan.nickname,
      from_current_period_end: billing.current_period_end_datetime,
      from_is_trial: billing.is_trial,
      from_is_limited: billing.is_limited,
      from_trial_start: billing.trial_start_datetime,
      from_trial_end: billing.trial_end_datetime,
    },
    {
      'Last Billing Change': new Date().toISOString(),
    },
    {
      'Billing Changes': 1,
    },
    done,
  )
}

export function trackChangelogButtonClick(): void {
  _track('Changelog Button Click')
}

export function trackChangelogItemClick(item: Changelog): void {
  _track('Changelog Item Click', { item: item.title })
}

export function trackChangelogLinkClick(url: string): void {
  _track('Changelog Link Click', { url })
}

export async function trackCardSave(billing: Billing): Promise<void> {
  // We'll track this event when the user comes back to the native app.
  if (router().currentRoute.value?.name === 'billing_apple_external') {
    return
  }

  try {
    const deviceInfo = await platform.deviceInfo()
    _track(
      'Card Save',
      {
        billing_type: billing.billing_type,
        stripe_payment_type: billing.stripe_payment_type,
      },
      {
        'Last Card Save': new Date().toISOString(),
      },
      {
        'Card Saved': 1,
      },
    )
    setOnce({
      'Card Save Start Time': new Date().toISOString(),
      'Card Save Device': deviceInfo.model,
      'Card Save Platform': deviceInfo.platform,
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

/**
 * Get "Card Save" event data to be tracked in the backend.
 */
export async function getCardSaveData(): Promise<Record<string, any>> {
  const superProperties = await getMixpanelSuperProperties()

  return {
    to_track: superProperties,
    to_set: {
      'Card Save Start Time': new Date().toISOString(),
      'Card Save Device': superProperties['Device Model'],
      'Card Save Platform': superProperties['Platform'],
    },
  }
}

export function trackCancelTrial(billing: Billing): void {
  _track('Trial Cancel', { billing_type: billing.billing_type })
}

export function trackStartTrialButton(plan: Plan, location: string): void {
  _track('Start Trial Button Clicked', { plan_id: plan.id, location })
}

export function trackAppStoreButton(plan: Plan): void {
  _track('App Store Button Clicked', { plan_id: plan.id })
}

export function trackExternalPaymentButton(plan: Plan): void {
  _track('External Payment Button Clicked', { plan_id: plan.id })
}

export function trackHesitationPopupOpen(trigger: string): void {
  _track('Hesitation Discount Modal Open', { trigger })
}

export function trackMoveBookToList(doc: DocListItem, list: string): void {
  _track(
    'Doc Move To List',
    {
      doc_type: doc.doc_type,
      doc_id: doc.id,
      doc_title: doc.title,
      doc_is_free: doc.is_free,
      'Doc Word Count': doc.summary_words,
      list: list,
    },
    {
      'Doc Move To List Last': new Date().toISOString(),
    },
    {
      'Doc Move to List Count': 1,
    },
  )
}

export function trackFavorite(
  doc: Pick<DocListItem, 'id' | 'title' | 'doc_type' | 'is_free'> & {
    summary_words?: number | null
  },
  is_favorite: boolean,
): void {
  const eventName = is_favorite ? 'Favorite' : 'Unfavorite'
  _track(
    `Doc ${eventName}`,
    {
      doc_type: doc.doc_type,
      doc_id: doc.id,
      doc_title: doc.title,
      doc_is_free: doc.is_free,
      'Doc Word Count': doc.summary_words,
      is_favorite: is_favorite,
    },
    {
      [`Doc ${eventName} Last`]: new Date().toISOString(),
    },
    {
      [`Doc ${eventName} Count`]: 1,
    },
  )
}

export function trackWantToRead(
  doc: DocListItem,
  docCustomLists: CustomList[],
  added: boolean,
): void {
  const eventName = added ? 'Add to Want to Read' : 'Remove from Want to Read'

  _track(
    `Doc ${eventName}`,
    {
      doc_type: doc.doc_type,
      doc_id: doc.id,
      doc_title: doc.title,
      doc_is_free: doc.is_free,
      'Doc Word Count': doc.summary_words,
      custom_lists: docCustomLists.map((list) => list.title),
    },
    {
      [`Doc ${eventName} Last`]: new Date().toISOString(),
    },
    {
      [`Doc ${eventName} Count`]: 1,
    },
  )
}

export function trackAddToCustomList(
  doc: DocListItem,
  docCustomLists: CustomList[],
): void {
  _track(
    'Doc Add to Custom List',
    {
      doc_type: doc.doc_type,
      doc_id: doc.id,
      doc_title: doc.title,
      doc_is_free: doc.is_free,
      'Doc Word Count': doc.summary_words,
      custom_lists: docCustomLists.map((list) => list.title),
    },
    {
      ['Doc Add to Custom List Last']: new Date().toISOString(),
    },
    {
      ['Doc Add to Custom List Count']: 1,
    },
  )
}

export function trackRemoveFromCustomList(
  doc: DocListItem,
  docCustomLists: CustomList[],
): void {
  _track(
    'Doc Remove from Custom List',
    {
      doc_type: doc.doc_type,
      doc_id: doc.id,
      doc_title: doc.title,
      doc_is_free: doc.is_free,
      'Doc Word Count': doc.summary_words,
      custom_lists: docCustomLists.map((list) => list.title),
    },
    {
      ['Doc Remove from Custom List Last']: new Date().toISOString(),
    },
    {
      ['Doc Remove from Custom List Count']: 1,
    },
  )
}

export function trackCreateCustomList(list: CustomList): void {
  _track(
    'Create Custom List',
    {
      id: list.id,
      title: list.title,
    },
    {
      ['Create Custom List Last']: new Date().toISOString(),
    },
    {
      ['Create Custom List Count']: 1,
    },
  )
}

export function trackDeleteCustomList(list: CustomList): void {
  _track(
    'Delete Custom List',
    {
      id: list.id,
      title: list.title,
    },
    {
      ['Delete Custom List Last']: new Date().toISOString(),
    },
    {
      ['Delete Custom List Count']: 1,
    },
  )
}

export function trackRenameCustomList(
  list: CustomList,
  oldTitle: string,
): void {
  _track(
    'Rename Custom List',
    {
      id: list.id,
      title: list.title,
      old_title: oldTitle,
    },
    {
      ['Rename Custom List Last']: new Date().toISOString(),
    },
    {
      ['Rename Custom List Count']: 1,
    },
  )
}

export function trackExerciseSave(
  content: ContentView,
  exercise: Exercise,
): void {
  if (exercise.submitted_at) {
    _track(
      'Exercise Submit',
      {
        book_id: content.doc.id,
        book_title: content.doc.title,
        book_is_free: content.doc.is_free,
        book_forum_id: content.doc.forum_id,
        exercise_id: exercise.id,
        exercise_submitted_at: exercise.submitted_at,
        content: exercise.content_id,
        content_id: content.id,
        content_book_id: content.doc_id,
        content_title: content.title,
        content_type: content.content_type,
        content_order: content.order,
      },
      {
        'Last Exercise Submit': new Date().toISOString(),
      },
      {
        'Exercises Submitted': 1,
      },
    )
  }
}

export function trackExerciseDiscuss(
  content: ContentView,
  exercise: Exercise,
): void {
  _track(
    'Exercise Discuss',
    {
      book_id: content.doc.id,
      book_title: content.doc.title,
      book_is_free: content.doc.is_free,
      book_forum_id: content.doc.forum_id,
      exercise_id: exercise.id,
      exercise_submitted_at: exercise.submitted_at,
      content: exercise.content_id,
      content_id: content.id,
      content_book_id: content.doc_id,
      content_title: content.title,
      content_type: content.content_type,
      content_order: content.order,
    },
    {
      'Last Exercise Discuss': new Date().toISOString(),
    },
    {
      'Exercise Discussed': 1,
    },
  )
}

export function trackNotifyNewThreadsToggle(active: boolean, doc: Doc): void {
  _track('Notify New Threads Toggle', {
    is_toggle_active: active,
    doc_type: doc.doc_type,
    doc_id: doc.id,
    doc_title: doc.title,
    doc_author: doc.author,
  })
}

export function trackNotifyNewCommentsToggle(
  active: boolean,
  doc: Doc,
  thread: Thread,
): void {
  _track('Notify New Comments Toggle', {
    is_toggle_active: active,
    doc_type: doc.doc_type,
    doc_id: doc.id,
    doc_title: doc.title,
    doc_author: doc.author,
    thread_id: thread.id,
    thread_title: thread.title,
  })
}

export function trackThreadCreate(book: Book, thread: ThreadWithAuthor): void {
  _track(
    'Thread Create',
    {
      book_id: book.id,
      book_title: book.title,
      book_is_free: book.is_free,
      book_forum_id: book.forum_id,
      thread_title: thread.title,
    },
    {
      'Last Thread Create': new Date().toISOString(),
    },
    {
      'Thread Created': 1,
    },
  )
}

export function trackThreadUpdate(book: Book, thread: ThreadWithAuthor): void {
  _track(
    'Thread Update',
    {
      book_id: book.id,
      book_title: book.title,
      book_is_free: book.is_free,
      book_forum_id: book.forum_id,
      thread_title: thread.title,
      thread_created: thread.created,
      thread_comments_count: thread.comments_count,
    },
    {
      'Last Thread Update': new Date().toISOString(),
    },
    {
      'Thread Updated': 1,
    },
  )
}

export function trackCommentCreate(
  doc: Doc,
  thread: Thread,
  comment: Comment,
): void {
  let eventName = 'Comment Create'
  const eventData: any = {
    thread_id: thread.id,
    thread_title: thread.title,
    thread_created: thread.created,
    thread_updated: thread.updated,
    thread_comments_count: thread.comments_count,
    comment_id: comment.id,
    comment_author_id: comment.author.id,
    comment_author_username: comment.author.username,
  }
  if (thread.author) {
    eventData['thread_author_id'] = thread.author.id
    eventData['thread_author_username'] = thread.author.username
  }
  if (doc.doc_type === 'book') {
    eventData['book_id'] = doc.id
    eventData['book_title'] = doc.title
    eventData['book_is_free'] = doc.is_free
    eventData['book_forum_id'] = doc.forum_id
  } else {
    eventName = 'Comment Create Article'
    eventData['article_id'] = doc.id
    eventData['article_title'] = doc.title
    eventData['article_is_free'] = doc.is_free
  }
  _track(
    eventName,
    eventData,
    {
      'Last Comment Create': new Date().toISOString(),
    },
    {
      'Comment Created': 1,
    },
  )
}

export function trackReplyCreate(
  doc: Doc,
  thread: Thread,
  comment: Comment,
  reply: Comment,
): void {
  const eventData: any = {
    thread_id: thread.id,
    thread_title: thread.title,
    thread_created: thread.created,
    thread_updated: thread.updated,
    thread_comments_count: thread.comments_count,
    comment_id: comment.id,
    comment_created: comment.created,
    comment_updated: comment.updated,
    comment_author_id: comment.author.id,
    comment_author_username: comment.author.username,
    reply_id: reply.id,
    reply_author_id: reply.author.id,
    reply_author_username: reply.author.username,
  }
  let eventName = 'Reply Create'
  if (thread.author) {
    eventData['thread_author_id'] = thread.author.id
    eventData['thread_author_username'] = thread.author.username
  }
  if (doc.doc_type === 'book') {
    eventData['book_id'] = doc.id
    eventData['book_title'] = doc.title
    eventData['book_is_free'] = doc.is_free
    eventData['book_forum_id'] = doc.forum_id
  } else {
    eventName = 'Reply Create Article'
    eventData['article_id'] = doc.id
    eventData['article_title'] = doc.title
    eventData['article_is_free'] = doc.is_free
  }
  _track(
    eventName,
    eventData,
    {
      'Last Reply Create': new Date().toISOString(),
    },
    {
      'Reply Created': 1,
    },
  )
}

export function trackHighlightCreate(
  content: AnnotationContent,
  annotation: Annotation,
): void {
  _track(
    'Highlight Create',
    {
      book_id: content.doc.id,
      content_id: content.id,
      content_title: content.title,
      highlight_start: annotation.ranges[0].startOffset,
      highlight_end: annotation.ranges[0].endOffset,
      highlight_has_note: annotation.text ? true : false,
      highlight_type: annotation.highlight_type,
    },
    {
      'Last Highlight Create': new Date().toISOString(),
    },
    {
      'Highlight Created': 1,
    },
  )
}

export function trackHighlightShare(
  content: ContentView,
  annotation: Annotation,
): void {
  _track(
    'Highlight Share',
    {
      book_id: content.doc_id,
      content_id: content.id,
      content_title: content.title,
      highlight_start: annotation.ranges[0].startOffset,
      highlight_end: annotation.ranges[0].endOffset,
      highlight_has_note: annotation.text ? true : false,
    },
    {
      'Last Highlight Share': new Date().toISOString(),
    },
    {
      'Highlight Shared': 1,
    },
  )
}

export function trackHighlightUpdate(
  content: AnnotationContent,
  annotation: Annotation,
): void {
  _track(
    'Highlight Update',
    {
      book_id: content.doc.id,
      content_id: content.id,
      content_title: content.title,
      highlight_id: annotation.id,
      highlight_start: annotation.ranges[0].startOffset,
      highlight_end: annotation.ranges[0].endOffset,
      highlight_has_note: annotation.text ? true : false,
      highlight_type: annotation.highlight_type,
    },
    {
      'Last Highlight Update': new Date().toISOString(),
    },
    {
      'Highlight Updated': 1,
    },
  )
}

export function trackHighlightDelete(
  content: AnnotationContent,
  annotation: Annotation,
): void {
  _track(
    'Highlight Delete',
    {
      book_id: content.doc.id,
      content_id: content.id,
      content_title: content.title,
      highlight_id: annotation.id,
      highlight_start: annotation.ranges[0].startOffset,
      highlight_end: annotation.ranges[0].endOffset,
      highlight_has_note: annotation.text ? true : false,
      highlight_type: annotation.highlight_type,
    },
    {
      'Last Highlight Delete': new Date().toISOString(),
    },
    {
      'Highlight Deleted': 1,
    },
  )
}

export function trackBookTagClick(tag: string): void {
  _track(
    'Select Book Category',
    {
      Category: tag,
    },
    {
      'Last Book Category Select': new Date().toISOString(),
    },
    {
      'Book Category Selected': 1,
    },
  )
}

export function trackSort(sortOption: DocSortOption): void {
  _track(
    'Sort Documents',
    {
      'Sort Option': DocSortOption[sortOption],
    },
    {
      'Last Sort': new Date().toISOString(),
    },
    {
      'Sort Used': 1,
    },
  )
}

/**
 * Track the Audio Listen event.
 *
 * Tracks Audio Listen event and updates 'Audio Listen Time' time profile
 * property.
 *
 * Args:
 *    book: the book that is listened to.
 *    timeDiff: number of seconds listened to.
 *    isDownload: if the audio is played back from a native storage download
 *    isOffline: whether the listening happened while offline
 */
export function trackAudioListen(
  book: DocListItem,
  timeDiff: number,
  isDownload: boolean,
  isOffline = offline.isOffline,
): void {
  const timeDiffInt = Math.round(timeDiff)
  if (timeDiffInt === 0) {
    return
  }
  _track(
    'Audio Listen',
    {
      // Event data
      audio_time_diff: timeDiffInt,
      book_id: book.id,
      book_title: book.title,
      book_is_free: book.is_free,
      is_download: isDownload,
      is_offline: isOffline,
    },
    {
      // People set data
      'Last Audio Listen': new Date().toISOString(),
    },
    {
      // People incremental data
      'Audio Listen Time': timeDiffInt,
    },
  )
}

export type AudioEntryPoint =
  | 'player' // Audio playback started via the play button on the reading-page audio player
  | 'mini_player' // Audio playback started in the mini player
  | 'media_session' // Audio playback started in an external media control such as lock screen, CarPlay, headset

// What path a user took to reach a guide before starting that guide's audio
// Applies only if the user did start a guide's audio using the reading-page player
export type AudioArrival =
  | 'card' // Guide was opened via a card (e.g. on a listing page)
  | 'preview' // Clicked read on the guide preview page
  | 'notification' // Push notification deep link
  | 'redirect' // Redirected to guide during onboarding
  | 'relaunch' // App restored previously open guide on open
  | 'in_guide' // We don't know; somehow they were in a guide

export interface AudioPlayAttribution {
  entryPoint: AudioEntryPoint
  arrival?: AudioArrival
  referrerPageName?: string
}

export interface AudioPlaybackMetrics {
  playbackId: string // UUID linking `Audio Play` to `Audio Stop` event
  startedAt: number // Timestamp for start of playback (milliseconds since Unix epoch)
  playedSeconds: number // Seconds of audio content played (as if played at 1x speed)
  listenedSeconds: number // User's wall-clock time spent with audio playing
}

export function trackAudioPlay(
  playback: AudioPlaybackMetrics,
  doc: Doc,
  chapterIndex: number,
  positionSeconds: number,
  playbackRate: number,
  attribution: AudioPlayAttribution,
  outputDevice?: AudioOutputDevice,
): void {
  _track('Audio Play', {
    'Playback ID': playback.playbackId,
    'Doc ID': doc.id,
    'Chapter Index': chapterIndex,
    'Position Seconds': positionSeconds,
    'Playback Rate': playbackRate,
    'Entry Point': attribution.entryPoint,
    ...(attribution.arrival && { Arrival: attribution.arrival }),
    ...(attribution.referrerPageName && {
      'Referrer Page Name': attribution.referrerPageName,
    }),
    ...(outputDevice && {
      'Audio Output Device Type': outputDevice.type,
      ...(outputDevice.name && {
        'Audio Output Device Name': outputDevice.name,
      }),
    }),
  })
}

export type AudioStopReason = 'pause' | 'ended' | 'navigate' | 'interrupt'

export function trackAudioStop(
  playback: AudioPlaybackMetrics,
  doc: Doc,
  chapterIndex: number,
  positionSeconds: number,
  playbackRate: number,
  reason: AudioStopReason,
  stoppedAt: number,
): void {
  _track('Audio Stop', {
    'Playback ID': playback.playbackId,
    'Doc ID': doc.id,
    'Chapter Index': chapterIndex,
    'Position Seconds': positionSeconds,
    'Playback Rate': playbackRate,
    'Played Seconds': playback.playedSeconds,
    'Listened Seconds': playback.listenedSeconds,
    Reason: reason,
    'Audio Time Difference': Math.floor(
      (stoppedAt - playback.startedAt) / 1000,
    ),
  })
}

const readTimeEvents = new Map([
  ['book', 'Book'],
  ['article', 'Article'],
  ['podcast_episode', 'PodcastEpisode'],
])

/**
 * Track the Read Time event.
 *
 * Tracks the event and updates '(Article | Book | Podcast Episode) Read Time' time profile
 * property.
 *
 * Args:
 *    doc: the doc that is read.
 *    timeDiff: number of seconds the book was read (open).
 *    sessionStartTime: when this reading session began, as a Unix timestamp.
 */
export async function trackDocReadTime(
  doc: DocView,
  timeDiff: number,
  sessionStartTime: number,
): Promise<void> {
  const timeDiffInt = Math.round(timeDiff)
  if (timeDiffInt === 0) {
    return
  }

  const docName = readTimeEvents.get(doc.doc_type)

  return await _trackAsync(
    `${docName} Read Time`,
    {
      // Event data
      time_diff: timeDiffInt,
      [`${doc.doc_type}_id`]: doc.id,
      [`${doc.doc_type}_url_slug`]: doc.url_slug,
      [`${doc.doc_type}_title`]: doc.title,
      [`${doc.doc_type}_is_free`]: doc.is_free,
      'Doc Word Count': doc.summary_words,
      'Reading Session Started At': new Date(sessionStartTime).toISOString(),
    },
    {
      // People set data
      [`Last ${docName} Read`]: new Date().toISOString(),
    },
    {
      // People incremental data
      [`Read Time ${docName}s`]: timeDiffInt,
    },
  )
}

export function trackAppReviewShow(): void {
  _track('App Review Show')
}

export function trackAppReviewFailure(): void {
  _track('App Review Failure')
}

/**
 * Track Done button click when finishing the book.
 *
 * Args:
 *    book: the book that is listened to.
 */
export function trackBookDoneClick(book: DocView): void {
  _track(
    'Book Done Click',
    {
      // Event data
      book_id: book.id,
      book_title: book.title,
      book_is_free: book.is_free,
      'Doc Word Count': book.summary_words,
    },
    {
      // People set data
      'Last Book Done Click': new Date().toISOString(),
    },
    {
      // People incremental data
      'Book Done Clicks': 1,
    },
  )
}

/**
 * Track that the button to download a book to native device storage was clicked.
 */
export function trackDeviceStorageDownload(book: Book): void {
  _track('Device Storage Download', {
    book_id: book.id,
    book_title: book.title,
  })
}

/**
 * Track that the button to delete a book from native device storage was clicked.
 */
export function trackDeviceStorageDelete(book: Book): void {
  _track('Device Storage Delete', {
    book_id: book.id,
    book_title: book.title,
  })
}

/**
 * Track the outcome of the "ask for notification permission" popup.
 * This event is sent on every app load for 'granted' and only once
 * on 'denied'.
 */
export function trackNotificationPermission(granted: boolean): void {
  _track('Push Notification Permission', { granted })
}

/**
 * Track when a push notification is opened.
 */
export function trackNotificationOpen(
  notification: NotificationType | undefined,
  event: string,
  link: string,
  delayed: boolean = false,
): void {
  // Remove anything before or after the `notification_source` URL parameter.
  const source = link.split('notification_source=')[1]?.replace(/&.+/, '')
  _track('Push Notification Open', {
    'Notification Data': notification,
    'Notification Source': source,
    'OS Event': event,
    'Link URL': link,
    Delayed: delayed,
  })
}

export function trackAccountDeleteClick(): void {
  _track('Account Delete Click')
}

export function trackAccountDeleteCancel(): void {
  _track('Account Delete Cancel')
}

export function trackAccountDeleteRequest(): void {
  _track('Account Delete Request')
}

export async function trackAccountDeleteConfim(): Promise<void> {
  _trackAsync('Account Delete Confirm')
}

/**
 * Track that an excerpt was clicked.
 */
export function trackExcerptClick(excerptData: ExcerptData): void {
  _track('Excerpt Click', {
    excerpt_id: excerptData.id,
    doc_url_slug: excerptData.doc_url_slug,
    content_url_slug: excerptData.content_url_slug,
  })
}

/**
 * Track when the Want to Read block becomes visible on Discover.
 */
export function trackWantToReadBlockImpression(listSize: number): void {
  _track('Want to Read Block Impression', {
    'Want to Read List Size': listSize,
  })
}

/**
 * Track the listing blocks rendered when the Discover page finishes loading.
 */
export function trackDiscoverPageLoaded(
  listingBlocks: Record<string, string[]>,
): void {
  _track('Discover Page Loaded', {
    'Listing Blocks': Object.keys(listingBlocks),
    ...listingBlocks,
  })
}

/**
 * Track document view event.
 *
 * @param event: event from the component wrapping the doc card.
 * @param referrerBlockTitle: title of the referrer block or listing page.
 * @param referrerUrl: URL of the referrer page.
 * @param doc: The doc that was clicked.
 * @param extraProperties: Extra properties for this event.
 */
export function trackDocView(
  event: Event,
  referrerBlockTitle: string,
  referrerUrl: string,
  doc: ListItem | DocListItem,
  extraProperties?: Record<string, unknown>,
): void {
  const eventTarget = event.target as HTMLElement

  const controlSelectors = ['collection', 'more-btn', 'action-menu']

  const isControlButton = controlSelectors.some((selector) => {
    return eventTarget.className?.includes(selector)
  })

  // Ignore controls on the card, such as the "Add to Favorites"
  // and action menu buttons.
  if (isControlButton) {
    return
  }

  _track(
    'Doc View',
    {
      // Event data
      'Referrer Block': referrerBlockTitle,
      'Referrer Page Name': referrerUrl,
      'Doc URL': doc.url_slug,
      'Doc Title': doc.title,
      'Doc Type': doc.doc_type,
      'Doc Progress': doc.user?.progress,
      'Doc First Published At': (doc as DocListItem).first_published_at,
      'Doc Is AI': !!(doc as DocListItem).is_ai,
      'Doc Word Count': (doc as DocListItem).summary_words,
      ...extraProperties,
    },
    {
      // People set data
      'Last Doc Open From Block': new Date().toISOString(),
    },
    {
      // People incremental data
      'Doc Opens From Block': 1,
    },
  )
}

/**
 * Track when a Shortform URL is handed to us by a deep link (eg. "shortform://"
 * or "https://www.shortform.com/...").
 */
export function trackDeepLinkClick(
  url: string,
  delayed: boolean = false,
): void {
  _track('Deep Link Click', {
    // Event data
    'Link URL': url,
    Delayed: delayed,
  })
}

/**
 * Track the selection of a link (internal or external) in document content.
 */
export function trackDocLinkClick(url: string, doc: DocView): void {
  _track('Doc Link Click', {
    // Event data
    'Doc URL': doc.url_slug,
    'Doc Title': doc.title,
    'Doc Type': doc.doc_type,
    'Doc Word Count': doc.summary_words,
    'Link URL': url,
  })
}

/**
 * Track completion of one page (book chapter).
 */
export function trackContentCompleted(doc: Doc): void {
  _track('Content Completed', {
    // Event data
    'Doc ID': doc.id,
    'Doc URL': doc.url_slug,
    'Doc Title': doc.title,
    'Doc Type': doc.doc_type,
    'Doc Word Count': doc.summary_words,
    'Doc Progress': doc.user.progress,
    'Completion Rule Version': 2,
  })
}

/**
 * Track completion of an entire document (book).
 */
export function trackDocCompleted(doc: Doc): void {
  _track('Doc Completed', {
    // Event data
    'Doc ID': doc.id,
    'Doc URL': doc.url_slug,
    'Doc Title': doc.title,
    'Doc Type': doc.doc_type,
    'Doc Word Count': doc.summary_words,
    'Completion Rule Version': 2,
  })
}

/** Track when the loaded completion popup becomes visible to the user. */
export function trackCompletionFlowShown(
  guideId: string,
  completionPath: CompletionPath,
): void {
  _track('Completion Flow Shown', {
    'Doc ID': guideId,
    'Completion Path': completionPath,
  })
}

/**
 * Track a guide reaching 100% and store the user's first completion as the
 * experiment enrollment anchor.
 */
export function trackCompletionExperimentGuideFinished(
  guideId: string,
  completionPath: CompletionPath,
  variant: Exclude<GuideCompletion19383Variant, null>,
): void {
  setOnce({
    'Guide Completion 19383 Enrollment Guide ID': guideId,
    'Guide Completion 19383 Enrollment Completion Path': completionPath,
    'Guide Completion 19383 Enrolled At': new Date().toISOString(),
  })
  _track('Completion Experiment Guide Finished', {
    'Doc ID': guideId,
    'Completion Path': completionPath,
    'Experiment Variant': variant,
  })
}

/** Track a guide opened after the user has joined the completion experiment. */
export function trackCompletionExperimentGuideOpened(
  guideId: string,
  variant: Exclude<GuideCompletion19383Variant, null>,
): void {
  _track('Completion Experiment Guide Opened', {
    'Doc ID': guideId,
    'Experiment Variant': variant,
  })
}

/** Track a recommendation card becoming visible in the completion popup. */
export function trackCompletionFlowRecommendationShown(
  guideId: string,
  recommendation: CompletionRecommendationData,
  position: number,
): void {
  _track('Completion Flow Recommendation Shown', {
    'Doc ID': guideId,
    'Destination Guide ID': recommendation.book.id,
    Category: recommendation.book.tags[0],
    Position: position,
  })
}

/**
 * Track an action taken in the completion popup, including destination details
 * when the action concerns a recommendation.
 */
export function trackCompletionFlowAction(
  guideId: string,
  action: CompletionFlowAction,
  recommendation?: { guideId: string; category: string; position: number },
): void {
  _track('Completion Flow Action', {
    'Doc ID': guideId,
    Action: action,
    ...(recommendation && {
      'Destination Guide ID': recommendation.guideId,
      Category: recommendation.category,
      Position: recommendation.position,
    }),
  })
}

/** Track a successful exercise save containing at least one answer. */
export function trackExerciseAnswerSaved(
  guideId: string,
  exerciseId: string,
): void {
  _track('Exercise Answer Saved', {
    'Doc ID': guideId,
    'Exercise ID': exerciseId,
  })
}

export function trackSearchUsage(): void {
  if (!hasMixpanel()) {
    return
  }

  const lastSearch = new Date().toISOString()
  mixpanel.people.set({
    'Last Search': lastSearch,
  })
  mixpanel.people.increment('Search Count', 1)
}

/**
 * Track progress through the onboarding sequence.
 */
export function trackOnboardingPageView(
  page: string,
  choices?: QuestionnaireUserChoices,
): void {
  _track('Onboarding Page View', {
    Page: page,
    Choices: choices,
  })
}

/**
 * Track "Skip for now" clicks on the onboarding plan/card capture screens
 * (exp_onboarding_plan_cc_capture_v2_20676, #20676).
 */
export function trackOnboardingBillingSkipClick(currentScreen: string): void {
  _track('Onboarding Billing Skip Click', {
    current_screen: currentScreen,
  })
}

/**
 * Track a successfully saved card on the onboarding plan/card capture
 * screen (exp_onboarding_plan_cc_capture_v2_20676, #20676) -- fired for both
 * the "Start Free Trial" button and Express Checkout (Apple/Google Pay),
 * since both save the card and start the trial the same way.
 */
export function trackOnboardingStartFreeTrial(planId: string): void {
  _track('Onboarding Start Free Trial', {
    plan_id: planId,
  })
}

/**
 * Track "Onboarding Complete" when the user clicks the final button of the
 * onboarding flow and gets redirected into the app.
 */
export function trackOnboardingComplete(): void {
  _track('Onboarding Complete')
}

/**
 * Track display and response to the First Guide popup.
 */
export function trackFirstGuideAction(
  book: BookListItem,
  currentRoute: string,
  action: 'open' | 'read-book' | 'about-book' | 'close',
): void {
  _track('First Guide Action', {
    'Current Route': currentRoute,
    doc_url_slug: book.url_slug,
    Action: action,
  })
}

/**
 * Track language switch.
 *
 * Args:
 *    language: the language user switched to.
 */
export function trackLanguageSwitch(language: string): void {
  _track(
    'Language Switch',
    {
      // Event data
      language: language,
    },
    {
      // People set data
      'Last Language Switch': new Date().toISOString(),
    },
    {
      // People incremental data
      'Language Switches': 1,
    },
  )
}

/**
 * Track clicks on call to action buttons in the home page.
 */
export function trackHomeCTAClick(
  cta_type: 'store_link' | 'review',
  cta_id: string,
): void {
  _track('Home CTA Click', {
    cta_type,
    cta_id,
  })
}

/**
 * Track when the homepage public search input receives focus.
 */
export function trackHomepageSearchInputClick(): void {
  _track(
    'Homepage Search Input Click',
    undefined,
    {
      'Last Homepage Search Input Click': new Date().toISOString(),
    },
    {
      'Homepage Search Input Click Count': 1,
    },
  )
}

/**
 * Track a stable hero search query that returned no public summaries.
 */
export function trackHomeHeroSearchNoResults(query: string): void {
  _track('Homepage Search No Results', {
    query,
  })
}

/**
 * Track clicks on call to action buttons on pilot pages.
 */
export function trackPilotCTAClick(
  cta_type: 'top_interested' | 'interest_form_submit',
  cta_action?: string,
): void {
  _track('Pilot CTA Click', {
    cta_type,
    cta_action,
  })
}

type SignupModalLayout = 'desktop' | 'mobile'

export interface SignupModalEventData {
  'Current Route': string
  Layout: SignupModalLayout
  'CTA Id': string
}

export function trackSignupModalOpen(data: SignupModalEventData): void {
  _track('Signup Modal Open', { ...data })
}

export function trackSignupModalClose(data: SignupModalEventData): void {
  _track('Signup Modal Close', { ...data })
}

/**
 * Track the clicks/expansion of FAQ items on the support or billing page.
 * @param type the FAQ type the item belongs to.
 * @param item the FAQ item question that was clicked on.
 */
export function trackFAQItemView(type: string, item: string): void {
  _track('FAQ Item View', {
    FAQ: type,
    'FAQ Item': item,
  })
}

export function trackEnableNotificationsBannerSeen(): void {
  _track('Enable Notifications Banner Seen')
}

export function trackEnableNotificationsBannerClick(): void {
  _track('Enable Notifications Banner Click')
}

export function trackEnableNotificationsBannerClose(
  timesDismissed: number,
): void {
  _track('Enable Notifications Banner Close', {
    'Times Dismissed': timesDismissed,
  })
}

/**
 * Set the "First <docType> <event>" property in the Mixpanel user.
 */
export async function setFirstDoc(
  event: 'Viewed' | 'Completed' | 'Summary Completed',
  docTitle: string,
  docType: ListItemType,
): Promise<void> {
  // The date when this feature was implemented.
  const implementationDate = new Date('2025-05-01T00:00Z').getTime()
  const userSignupDate = assertNotNull(auth.getSignupDate()).getTime()

  // Exit early if the user signed up before this feature was implemented,
  // as it's likely that this is not the first doc interacted with by this user.
  if (implementationDate > userSignupDate) {
    return
  }

  const deviceInfo = await platform.deviceInfo()
  const docLabel = capitalizeString(docTypeLabel[docType].singular)

  const propertiesToSet = {
    [`First ${docLabel} ${event}`]: docTitle,
    [`First ${docLabel} ${event} Device`]: deviceInfo.model,
    [`First ${docLabel} ${event} Platform`]: deviceInfo.platform,
  }

  if (event !== 'Summary Completed') {
    propertiesToSet[`First Content ${event} Device`] = deviceInfo.model
    propertiesToSet[`First Content ${event} Platform`] = deviceInfo.platform
  }

  setOnce(propertiesToSet)
}

export function trackChecklistStepClicked(stepId: string): void {
  _track('Checklist Step Clicked', {
    'Step ID': stepId,
    Stratum: checklist()?.stratum,
  })
}

export function trackChecklistStepCompleted(stepId: string): void {
  _track('Checklist Step Completed', {
    'Step ID': stepId,
    Stratum: checklist()?.stratum,
  })
}

export function trackChecklistCompleted(): void {
  _track('Checklist Completed', {
    Stratum: checklist()?.stratum,
  })
}

export function trackChecklistShown(): void {
  _track('Checklist Shown', {
    Stratum: checklist()?.stratum,
  })
  setOnce({ 'Checklist Shown': true })
}

export function trackChecklistDismissed(): void {
  _track('Checklist Dismissed', {
    Stratum: checklist()?.stratum,
  })
}

/**
 * Track that the Mini Player appeared on the screen. (#19040)
 */
export function trackMiniPlayerShown(
  doc: Doc,
  playerState: 'playing' | 'paused' | 'resume',
): void {
  _track('Mini Player Shown', {
    'Player State': playerState,
    'Doc ID': doc.id,
    'Doc Slug': doc.url_slug,
    'Doc Progress': doc.user.progress,
  })
}

/**
 * Track a click on the Mini Player, which opens the doc. (#19040)
 */
export function trackMiniPlayerClick(
  doc: Doc,
  playerState: 'playing' | 'paused' | 'resume',
): void {
  _track('Mini Player Click', {
    'Player State': playerState,
    'Doc ID': doc.id,
    'Doc Slug': doc.url_slug,
    'Doc Progress': doc.user.progress,
  })
}

/**
 * Track starting the playback from the Mini Player. (#19040)
 */
export function trackMiniPlayerPlay(
  doc: Doc,
  playerState: 'playing' | 'paused' | 'resume',
): void {
  _track('Mini Player Play', {
    'Player State': playerState,
    'Doc ID': doc.id,
    'Doc Slug': doc.url_slug,
    'Doc Progress': doc.user.progress,
  })
}

/**
 * Track pausing the playback from the Mini Player. (#19040)
 */
export function trackMiniPlayerPause(
  doc: Doc,
  playerState: 'playing' | 'paused',
): void {
  _track('Mini Player Pause', {
    'Player State': playerState,
    'Doc ID': doc.id,
    'Doc Slug': doc.url_slug,
    'Doc Progress': doc.user.progress,
  })
}

/**
 * Track dismissing the Mini Player. (#19040)
 */
export function trackMiniPlayerDismiss(
  doc: Doc,
  playerState: 'playing' | 'paused' | 'resume',
): void {
  _track('Mini Player Dismiss', {
    'Player State': playerState,
    'Doc ID': doc.id,
    'Doc Slug': doc.url_slug,
    'Doc Progress': doc.user.progress,
  })
}

/**
 * Track the event and optionally set people properties.
 *
 * Minimal example:
 *
 *   _track('My Event')
 *
 * Full example:
 *
 *   _track(
 *     // Event to track.
 *     'Select Book Category',
 *     // Event data.
 *     {
 *       category: tag,
 *     },
 *     // Data for mixpanel.people.set({..}).
 *     {
 *       'Last Book Category Select': new Date().toISOString(),
 *     },
 *     // Data for mixpanel.people.increment({..}).
 *     {
 *       'Book Category Selected': 1,
 *     },
 *     // Callback to execute after tracking.
 *     () => {
 *       console.log('done')
 *     }
 *   )
 */
export function _track(
  event: string,
  eventData?: Record<string, unknown>,
  peopleSetData?: Record<string, unknown>,
  peopleIncrementData?: Record<string, unknown>,
  callback?: (err?: any) => void,
): void {
  if (!hasMixpanel()) {
    return
  }

  _setSuperProperties()
    .then(() => {
      baseTrack(event, eventData, peopleSetData, peopleIncrementData, callback)
    })
    .catch((error) => {
      Sentry.captureException(error)
      baseTrack(event, eventData, peopleSetData, peopleIncrementData, callback)
    })
}

/**
 * Async version of the '_track' call.
 *
 * Note: this is better in terms of logic - most of the time we don't await the
 * tracking is it is not super essentaial, also this is not good for tests
 * - the calls left without wating for them and might be executed at
 * the point where the call will fail.
 * For example, in the trackBookReadTime test, where the tracking call is
 * invoked from the beforeRouteLeave it resulted in `book` parameter being
 * null (although we defenitely set it when we invoke the method).
 * Adding `async` helped solving it, but we still have many such cases with
 * other tracking calls.
 */
export async function _trackAsync(
  event: string,
  eventData?: Record<string, unknown>,
  peopleSetData?: Record<string, unknown>,
  peopleIncrementData?: Record<string, unknown>,
): Promise<void> {
  await _setSuperProperties()
  return await baseTrackAsync(
    event,
    eventData,
    peopleSetData,
    peopleIncrementData,
  )
}

/**
 * Set Mixpanel super properties (properties that are added to every `mixpanel.track` request).
 */
export async function _setSuperProperties(): Promise<void> {
  if (!hasMixpanel()) {
    return
  }

  const superProperties = await getMixpanelSuperProperties()

  mixpanel.register(superProperties)
}
